﻿/**
 * useWatchdog — Supervisor de saúde do Player.
 *
 * Roda um loop de verificação a cada 5 segundos e detecta duas categorias
 * de anomalia:
 *
 *  ┌──────────────┬───────────────────────────────────────────────────────┐
 *  │ VÍDEO        │ 1. Pausado sem comando do usuário                     │
 *  │              │ 2. currentTime parado (frame congelado)               │
 *  ├──────────────┼───────────────────────────────────────────────────────┤
 *  │ IMAGEM       │ Exibição acima de 3× a duração esperada               │
 *  └──────────────┴───────────────────────────────────────────────────────┘
 *
 * Processo de recuperação (escalonado):
 *  Tentativa 1 → avança para o próximo slide
 *  Tentativa 2 → window.location.reload()
 */

import { useEffect, useRef } from 'react';
import type React from 'react';
import { watchdogLog }       from '@/shared/lib/watchdogLog';
import type { Midia }        from '@/shared/models/midia.model';
import type { WatchdogEvent } from '@/shared/models/tela.model';

export type { WatchdogEvent } from '@/shared/models/tela.model';

const WATCHDOG_INTERVAL_MS   = 5_000;
const VIDEO_FROZEN_EPSILON   = 0.5;
const IMAGE_STUCK_MULTIPLIER = 3;
const GRACE_PERIOD_MS        = 5_000;

interface UseWatchdogOptions {
  currentMidia:     Midia | null;
  videoRef:         React.RefObject<HTMLVideoElement | null>;
  slideStartTimeRef: React.RefObject<number>;
  onAdvance:        () => void;
  telaId:           string;
}

export function useWatchdog({
  currentMidia,
  videoRef,
  slideStartTimeRef,
  onAdvance,
  telaId,
}: UseWatchdogOptions): void {

  const stuckCountRef     = useRef(0);
  const lastVideoTimeRef  = useRef(0);
  const slideStartedAtRef = useRef(Date.now());
  const currentMidiaRef   = useRef(currentMidia);
  const onAdvanceRef      = useRef(onAdvance);
  useEffect(() => { currentMidiaRef.current = currentMidia; }, [currentMidia]);
  useEffect(() => { onAdvanceRef.current    = onAdvance;    }, [onAdvance]);

  // Suprime o Watchdog durante a transição de fullscreen
  useEffect(() => {
    let earlyCheckTimer: ReturnType<typeof setTimeout> | null = null;
    let graceEndTimer:   ReturnType<typeof setTimeout> | null = null;
    let listeningVideo:  HTMLVideoElement | null = null;

    function tryResume() {
      const video = videoRef.current;
      if (video && video.paused && !video.ended && !video.seeking) {
        video.play().catch(() => {});
      }
    }

    function stopListening() {
      listeningVideo?.removeEventListener('pause', tryResume);
      listeningVideo = null;
      if (graceEndTimer)   { clearTimeout(graceEndTimer);   graceEndTimer   = null; }
      if (earlyCheckTimer) { clearTimeout(earlyCheckTimer); earlyCheckTimer = null; }
    }

    function onFullscreenChange() {
      const video = videoRef.current;
      if (video) lastVideoTimeRef.current = video.currentTime;
      slideStartedAtRef.current = Date.now();
      stuckCountRef.current = 0;
      stopListening();
      if (video) {
        listeningVideo = video;
        video.addEventListener('pause', tryResume);
      }
      earlyCheckTimer = setTimeout(tryResume, 300);
      graceEndTimer   = setTimeout(stopListening, 3_000);
    }

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      stopListening();
    };
  }, [videoRef]);

  // Retoma vídeo ao voltar do background
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      const video = videoRef.current;
      if (video && video.paused && !video.ended) {
        slideStartedAtRef.current = Date.now();
        stuckCountRef.current = 0;
        video.play().catch(() => {});
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [videoRef]);

  // Reset dos contadores quando a mídia ativa muda
  useEffect(() => {
    stuckCountRef.current     = 0;
    lastVideoTimeRef.current  = 0;
    slideStartedAtRef.current = Date.now();
  }, [currentMidia?.id]);

  // Loop principal
  useEffect(() => {
    const interval = setInterval(() => {
      // Quando a aba está em background o browser throttla o decode de vídeo:
      // currentTime para de avançar → Watchdog detectaria "video_frozen" como
      // falso positivo e chamaria reload(). Suspende todas as verificações
      // enquanto a página não está visível e reseta os contadores para que
      // o próximo tick após o retorno comece com estado limpo.
      if (document.visibilityState !== 'visible') {
        if (videoRef.current) lastVideoTimeRef.current = videoRef.current.currentTime;
        slideStartedAtRef.current = Date.now();
        stuckCountRef.current = 0;
        return;
      }

      const midia = currentMidiaRef.current;
      if (!midia) return;

      const timeSinceSlideStart = Date.now() - slideStartedAtRef.current;
      if (timeSinceSlideStart < GRACE_PERIOD_MS) {
        if (videoRef.current) lastVideoTimeRef.current = videoRef.current.currentTime;
        return;
      }

      let stuck = false;
      let stuckType: WatchdogEvent['type'] = 'image_stuck';

      if (midia.tipo === 'video') {
        const video = videoRef.current;
        if (!video) return;

        if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
          lastVideoTimeRef.current = video.currentTime;
          stuckCountRef.current = 0;
          return;
        }

        if (video.paused && !video.ended && !video.seeking) {
          stuck     = true;
          stuckType = 'video_paused';
        } else if (!video.paused) {
          const delta = video.currentTime - lastVideoTimeRef.current;
          if (delta >= 0 && delta < VIDEO_FROZEN_EPSILON) {
            stuck     = true;
            stuckType = 'video_frozen';
          }
        }

        lastVideoTimeRef.current = video.currentTime;

      } else {
        const elapsed   = Date.now() - (slideStartTimeRef.current ?? Date.now());
        const threshold = midia.duracao_segundos * 1000 * IMAGE_STUCK_MULTIPLIER;
        if (elapsed > threshold) {
          stuck     = true;
          stuckType = 'image_stuck';
        }
      }

      if (!stuck) { stuckCountRef.current = 0; return; }

      stuckCountRef.current++;
      const action: WatchdogEvent['action'] =
        stuckCountRef.current <= 1 ? 'advance' : 'reload';

      watchdogLog.push({ type: stuckType, midiaId: midia.id, telaId, action });

      console.warn(
        `[Watchdog] ${stuckType} (tentativa ${stuckCountRef.current}) → ${action}`,
        { midiaId: midia.id, currentTime: videoRef.current?.currentTime },
      );

      if (action === 'advance') {
        onAdvanceRef.current();
      } else {
        window.location.reload();
      }
    }, WATCHDOG_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [telaId, videoRef, slideStartTimeRef]);
}
